/**
 * Recently viewed cities in the header menus.
 * ---------------------------------------------------------------------------
 *
 * The CUBE / FRAME / GRAND / BLOK rows in the mega-menu and the mobile sidebar
 * are merchandised server-side out of `featured_cities_by_country` — the top
 * cities for the visitor's country. That list is fixed at render time, and
 * Liquid has no way of knowing which city the visitor just looked at (there is
 * no cookie access, and the city is usually not in the featured set anyway —
 * Aachen never outranks Berlin).
 *
 * So the shift happens here instead. Two halves:
 *
 *   record()  The product pages emit a small JSON blob for the ONE variant they
 *             render (all four configurators resolve `?variant=` server-side,
 *             so a page view is a city view). GRAND is the exception — its city
 *             `<select>` swaps in place without navigating, so it calls
 *             record() directly on change.
 *
 *   inject()  Every page: prepend a card per recent city to each marked row,
 *             newest first, and drop as many curated cards off the end. Cards
 *             are CLONED from the row's own first card rather than built from a
 *             template string, so each context keeps its exact markup — the
 *             desktop grid's `h6 text-center` label, the sidebar's `smallcaps`
 *             one, GRAND's -26% crop — without any of it being duplicated here.
 *
 * Entries expire after 30 days: long enough that a visitor returning next week
 * still finds the city they were looking at, short enough that a link to a
 * variant that has since been pulled does not linger forever.
 */
(() => {
  const KEY = 'cf:recent-cities';
  const DAY_MS = 24 * 60 * 60 * 1000;

  // Catalogue cities keep for a month. Clearance passes its own, shorter, value
  // — those items are a unit or two deep and an entry that outlives its stock
  // would leave a sold-out page sitting in a merchandising slot.
  const DEFAULT_TTL_DAYS = 30;

  // Rows show at most 10 cards (desktop CUBE), so anything past that can never
  // be displayed. A little headroom absorbs entries that dedupe against the
  // curated cards.
  const MAX_PER_BRAND = 16;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /**
   * A row holds one card per CITY, never one per area — the curated loop takes
   * `city.cube_variant | first`, so Berlin is one card even though five Berlin
   * areas exist. Matching on the variant id alone would break that: view
   * "Berlin - Museum Island" (a clearance area, or just another area of the
   * regular product) while Berlin is merchandised and the row ends up with two
   * Berlin cards, same label, different picture. Dedupe on the city name, which
   * every side takes from the same metaobject field.
   */
  function cityKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  /** { cube: [{ id, h: handle, c: city, i: image url, t: timestamp, ttl }], ... }, newest first. */
  function read() {
    let raw;

    try {
      raw = JSON.parse(localStorage.getItem(KEY) || '{}');
    } catch (e) {
      return {}; // Corrupt payload, or Safari private mode
    }

    const now = Date.now();
    const store = {};

    for (const brand of Object.keys(raw)) {
      if (!Array.isArray(raw[brand])) continue;
      store[brand] = raw[brand]
        .filter((entry) => {
          if (!entry || !entry.id || !entry.c) return false;
          return now - entry.t < (entry.ttl || DEFAULT_TTL_DAYS) * DAY_MS;
        })
        .slice(0, MAX_PER_BRAND);
    }

    return store;
  }

  function record(entry) {
    if (!entry || !entry.brand || !entry.id || !entry.city || !entry.handle) return;

    const store = read();
    const id = String(entry.id);

    // Re-viewing a city moves it back to the front rather than duplicating it.
    // Matching on the city too, so the clearance Berlin and the catalogue Berlin
    // never both sit in the list waiting for the row to drop one of them.
    const list = (store[entry.brand] || []).filter(
      (item) => String(item.id) !== id && cityKey(item.c) !== cityKey(entry.city)
    );

    const next = { id: id, h: entry.handle, c: entry.city, i: entry.image || '', t: Date.now() };
    if (entry.ttlDays) next.ttl = entry.ttlDays;

    list.unshift(next);
    store[entry.brand] = list.slice(0, MAX_PER_BRAND);

    try {
      localStorage.setItem(KEY, JSON.stringify(store));
    } catch (e) {
      // Safari in private mode does not allow setting item, we silently fail
    }
  }

  /**
   * Swap the `width=` of a CDN image URL. The stored URL is emitted at width
   * 600; the row needs whatever widths its own srcset already asks for.
   */
  function sizedUrl(url, width) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function buildCard(template, entry) {
    const card = template.cloneNode(true);

    card.href = root + 'products/' + entry.h + '?variant=' + entry.id;
    card.dataset.cfVariant = entry.id;
    card.dataset.cfRecent = 'true';

    const image = card.querySelector('img');

    if (image) {
      const widths = (image.getAttribute('srcset') || '').match(/\d+(?=w)/g);
      const srcWidth = (image.getAttribute('src') || '').match(/[?&]width=(\d+)/);

      // The curated cards carry the media's full width in `src` and let the
      // srcset do the real work. No reason to repeat that here — the largest
      // srcset candidate is the widest size the row can ever ask for.
      image.setAttribute(
        'src',
        sizedUrl(entry.i, widths ? widths[widths.length - 1] : srcWidth ? srcWidth[1] : 400)
      );

      if (widths) {
        image.setAttribute(
          'srcset',
          widths.map((width) => sizedUrl(entry.i, width) + ' ' + width + 'w').join(', ')
        );
      } else {
        image.removeAttribute('srcset');
      }

      image.setAttribute('alt', entry.c);
      image.setAttribute('loading', 'lazy');

      // The intrinsic size came from the card we cloned and does not describe
      // this image. The rows size every card by CSS aspect-ratio anyway.
      image.removeAttribute('width');
      image.removeAttribute('height');
    }

    const label = card.querySelector('p');
    if (label) label.textContent = entry.c;

    return card;
  }

  function injectRow(row, store) {
    const entries = store[row.dataset.cfRecentRow];
    if (!entries || entries.length === 0) return;

    const cards = Array.from(row.querySelectorAll(':scope > [data-cf-card]'));
    if (cards.length === 0) return;

    // Hold the insertion point before any removal — once every curated card has
    // deduped away there is nothing left to insert in front of, and the sidebar
    // rows end with a "more available" link that must stay last.
    const marker = document.createComment('recently viewed');
    row.insertBefore(marker, cards[0]);

    const limit = cards.length;
    const seen = new Set();
    const fresh = [];

    for (const entry of entries) {
      // No image means the clone would keep the template card's picture under
      // someone else's city name. Better to leave the row alone.
      if (!entry.i) continue;
      if (seen.has(cityKey(entry.c))) continue;
      seen.add(cityKey(entry.c));
      fresh.push(buildCard(cards[0], entry));
      if (fresh.length >= limit) break;
    }

    // A recent city that is also merchandised moves to the front rather than
    // appearing twice.
    cards.forEach((card) => {
      if (seen.has(cityKey(card.dataset.cfCity))) card.remove();
    });

    // Every entry skipped (all imageless): leave the row, and its heading, alone.
    if (fresh.length === 0) {
      marker.remove();
      return;
    }

    fresh.forEach((card) => row.insertBefore(card, marker));
    marker.remove();

    Array.from(row.querySelectorAll(':scope > [data-cf-card]'))
      .slice(limit)
      .forEach((card) => card.remove());

    // "Popular cities" stops being true the moment the visitor's own city leads
    // the row. The replacement copy is translated in Liquid and parked on the
    // heading — this side only picks between two strings it was handed.
    const heading = row.parentElement && row.parentElement.querySelector('[data-cf-recent-heading]');
    if (heading && heading.dataset.cfRecentHeading) {
      heading.textContent = heading.dataset.cfRecentHeading;
    }
  }

  function inject() {
    const rows = document.querySelectorAll('[data-cf-recent-row]');
    if (rows.length === 0) return;

    const store = read();
    rows.forEach((row) => injectRow(row, store));
  }

  /**
   * The CUBE clearance product swaps city in place through the standard variant
   * picker, which rewrites `?variant=` and fires `variant:change` on the form.
   * The event payload carries the variant title ("Berlin - Museum Island"), not
   * the metaobject name the rows dedupe on, so the city and image come from a
   * map Liquid rendered instead. Sold-out variants are absent from that map.
   */
  function watchVariantPicker() {
    const node = document.querySelector('script[data-cf-recent-city-map]');
    if (!node) return;

    let map;

    try {
      map = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }

    if (!map || !map.variants) return;

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      const match = variant && map.variants[String(variant.id)];
      if (!match) return;

      record({
        brand: map.brand,
        id: variant.id,
        handle: map.handle,
        city: match.city,
        image: match.image,
        ttlDays: map.ttlDays
      });
    });
  }

  window.CFRecentCities = { record: record, read: read };

  function start() {
    // Record before injecting, so the city on screen leads its own row.
    const blob = document.querySelector('script[data-cf-recent-city]');

    if (blob) {
      try {
        record(JSON.parse(blob.textContent));
      } catch (e) {
        // Malformed blob must not take the menus down with it
      }
    }

    watchVariantPicker();

    // A task late, not a frame: GRAND records its city from its own
    // DOMContentLoaded handler (the selection lives in a `<select>`, not in the
    // URL) and that handler is registered after this one, so injecting inline
    // would miss it. requestAnimationFrame would be the natural choice but it
    // never fires in a background tab, and a menu built on the next visit to
    // that tab still has to be right. The menus are closed either way.
    setTimeout(inject, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
